import React from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const BookmarkDrawer = ({
  open,
  onClose,
  bookmarks = [],
  currentChapterId,
  currentVerse = 1,
  isCurrentVerseBookmarked = false,
  onToggleCurrentBookmark,
  onSelectBookmark,
  onRemoveBookmark,
  edition,
  isMobile = false,
}) => {
  const isEn = edition?.id === "en";

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : 380,
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BookmarkIcon color="primary" fontSize="small" />
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
            {isEn ? "Bookmarks" : "阅读书签"} ({bookmarks.length})
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {onToggleCurrentBookmark && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Button
            fullWidth
            size="small"
            variant={isCurrentVerseBookmarked ? "outlined" : "contained"}
            color="primary"
            startIcon={<BookmarkAddOutlinedIcon fontSize="small" />}
            onClick={onToggleCurrentBookmark}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            {isCurrentVerseBookmarked
              ? isEn
                ? `Remove Bookmark (Verse ${currentVerse})`
                : `移除当前节书签（第 ${currentVerse} 节）`
              : isEn
                ? `Bookmark Current Verse (Verse ${currentVerse})`
                : `添加当前节书签（第 ${currentVerse} 节）`}
          </Button>
        </Box>
      )}

      <Divider />

      <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
        {bookmarks.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {isEn
                ? "No bookmarks yet. Add one to return anytime."
                : "暂无书签，点击上方按钮即可记录当前读经位置"}
            </Typography>
          </Box>
        ) : (
          bookmarks.map((bm) => {
            const isCurrentChapter = bm.chapterId === currentChapterId;
            return (
              <Box
                key={bm.id}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isCurrentChapter ? "primary.main" : "divider",
                  bgcolor: "background.paper",
                  display: "flex",
                  alignItems: "stretch",
                  overflow: "hidden",
                }}
              >
                <ButtonBase
                  onClick={() => {
                    if (onSelectBookmark) onSelectBookmark(bm);
                    if (onClose) onClose();
                  }}
                  sx={{
                    flex: 1,
                    p: 1.25,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    textAlign: "left",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 0.4,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: "0.88rem" }}>
                      {bm.bookName} {bm.chapterNumber}:{bm.verse}
                    </Typography>
                    {isCurrentChapter && (
                      <Chip
                        label={isEn ? "Current" : "本章"}
                        size="small"
                        color="primary"
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                    )}
                  </Box>
                  {bm.verseText && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.4,
                      }}
                    >
                      {bm.verseText}
                    </Typography>
                  )}
                </ButtonBase>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    pr: 0.75,
                  }}
                >
                  <Tooltip title={isEn ? "Remove bookmark" : "删除书签"}>
                    <IconButton
                      size="small"
                      aria-label={isEn ? "Remove bookmark" : "删除书签"}
                      onClick={() => onRemoveBookmark && onRemoveBookmark(bm.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Drawer>
  );
};

export default BookmarkDrawer;
